import { Color, Spacing } from '@persona-id/design-tokens';
import { FilterIcon, LayersIcon } from '@persona-id/design-tokens/icons';
import { FC } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

import { VerificationTemplateFilter } from 'dashboard/components/VerificationAnalytics';
import DateGranularitySelector from 'dashboard/components/VerificationAnalytics/DateGranularitySelector';
import { VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { Period } from 'dashboard/lib/unixTime';
import { DatumWithTimestamp } from 'dashboard/lib/utils/charting';
import {
  DynamicFilters,
  VerificationStatusSummary,
  VerificationVolumeDatum,
  VerificationVolumeIntervals,
} from 'dashboard/models/metrics-verification';
import {
  FilterRulesForType,
  VerificationMetricsState,
} from 'dashboard/redux/slices/metrics-verification';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import { EmptyState } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/EmptyState';
import FilterPanel from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/FilterPanel';
import MetricsSummary from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/MetricsSummary';
import {
  SectionTitle,
  StyledMetricsCard,
  SummaryCardBody,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/style';
import {
  DropdownItem,
  FilterDimensions,
  FilteredVerificationType,
  PERIODICTY_MAP,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import VerificationMetricsChart from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationMetricsChart';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import { ApiModel, VerificationType } from 'lib/interfaces/api';
import { FontWeight } from 'lib/interfaces/ThemeSerialized';

export const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  row-gap: ${Spacing[5]};
  margin-left: auto;
  justify-content: right;
}
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

// Leave a little more space on the right side, since the icon itself has built in padding left
const StyledButton = styled(Button)`
  padding: ${Spacing[2]} ${Spacing[3]} ${Spacing[2]} ${Spacing[2]};
  margin: ${Spacing[2]};
`;

const StyledFilterPanel = styled(FilterPanel)`
  position: absolute;
  overflow: visible;
`;

const StyledTitle = styled(SectionTitle)`
  margin-right: -${Spacing[2]};
`;

// The types that allow filtration + grouping
export const FILTER_TYPES = [
  ApiModel.VerificationGovernmentId,
  ApiModel.VerificationSelfie,
  ApiModel.VerificationDatabase,
  ApiModel.VerificationAamva,
];

interface MetricsCardProps extends Pick<VerificationMetricsState, 'lastQueryFilters'> {
  barKeys: Partial<Record<'created_timestamp' | 'processed' | 'pass-rate' | 'group', string>>;
  data: VerificationStatusSummary;
  emptyData: VerificationStatusSummary;
  filledProcessedDataForVerificationType: (Omit<VerificationVolumeDatum, 'created_timestamp'> &
    DatumWithTimestamp)[];
  filterOrRulesForType: FilterRule[][];
  groupByDimensionForType: FilterDimensions;
  groupItemsForType: DropdownItem[];
  groupedByInquiries: boolean;
  groupedVerificationType: boolean;
  handleAddAndRule: (type: FilteredVerificationType, orRuleIndex: number) => void;
  handleAddOrRule: (type: FilteredVerificationType) => void;
  handleBulkRulesUpdate: (filterRules: Record<VerificationType, FilterRulesForType>) => void;
  handleDeleteAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => void;
  handleVerificationTemplateFilterUpdate: (
    verificationType: VerificationType,
    verificationTemplateIds: string[],
  ) => void;
  handleLoadMetrics: () => void;
  handleOpenFilters: (value: boolean) => void;
  handleUpdateAndRule: (
    type: VerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  handleUpdateGranularity: (type: VerificationType, period: Period) => void;
  isOutsideClickEnabled: boolean;
  lineKeys: Partial<Record<string, string>>;
  orderedProcessedLineKeys: Record<string, string>;
  processedBarKeys: Record<string, string>;
  selectedVerificationTemplates: string[];
  setFilterDropdown: (value: VerificationType | undefined) => void;
  setIsOutsideClickEnabled: (value: ((prevState: boolean) => boolean) | boolean) => void;
  showFilterDropdown: any;
  topValues: string[];
  topValuesVolume: string[];
  verificationTemplatesOptionsForType: Array<{ name: string; id: string }>;
  verificationType: VerificationType;
  verificationVolumeOverTime: VerificationVolumeIntervals;
  volumeLastQueryFilterRules: Record<VerificationType, FilterRulesForType>;
  dynamicFilterOptions: DynamicFilters;
  granularityOption: Period;
}

// Selection will be dictated by font weight + background color
const StyledDropdownMenuItem = styled(DropdownMenu.Item)<{ selected: boolean }>`
  font-weight: ${({ selected }) => (selected ? FontWeight.Medium : FontWeight.Regular)};
  background-color: ${({ selected }) => (selected ? Color.CoreSlate50 : 'transparent')};
`;

const MetricsCard: FC<MetricsCardProps> = ({
  barKeys,
  data,
  emptyData,
  filledProcessedDataForVerificationType,
  filterOrRulesForType,
  groupByDimensionForType,
  groupItemsForType,
  groupedByInquiries,
  groupedVerificationType,
  handleAddAndRule,
  handleAddOrRule,
  handleBulkRulesUpdate,
  handleDeleteAndRule,
  handleVerificationTemplateFilterUpdate,
  handleLoadMetrics,
  handleOpenFilters,
  handleUpdateAndRule,
  handleUpdateGranularity,
  isOutsideClickEnabled,
  lineKeys,
  orderedProcessedLineKeys,
  processedBarKeys,
  selectedVerificationTemplates,
  setFilterDropdown,
  setIsOutsideClickEnabled,
  showFilterDropdown,
  topValues,
  topValuesVolume,
  verificationTemplatesOptionsForType,
  verificationType,
  verificationVolumeOverTime,
  volumeLastQueryFilterRules,
  lastQueryFilters,
  dynamicFilterOptions,
  granularityOption,
}) => {
  const isEmptyData = data === emptyData;
  const verificationTypeTitle = VERIFICATION_TYPE_NAMES_SHORT[verificationType];
  const shouldDisplayFiltersForType = FILTER_TYPES.includes(verificationType);
  const hasValidFilterOrRulesForType =
    filterOrRulesForType.length > 0 &&
    !(
      filterOrRulesForType.length === 1 &&
      (filterOrRulesForType[0].length === 0 || Object.keys(filterOrRulesForType[0][0]).length === 0)
    );

  const filterRulesCount = hasValidFilterOrRulesForType ? ` (${filterOrRulesForType.length})` : '';
  const filterButtonCopy = `Filters${filterRulesCount}`;

  const selectedGroupByOption = groupByDimensionForType
    ? ` (${
        groupItemsForType.find((element: DropdownItem) => {
          return element.id === groupByDimensionForType;
        })?.name
      })`
    : '';
  const groupByButtonCopy = `Group${selectedGroupByOption}`;

  return isEmptyData ? (
    <>
      <EmptyState
        verificationTypeTitle={verificationTypeTitle}
        emptyData={emptyData}
        verificationType={verificationType}
        handleOpenFilters={() => {
          setFilterDropdown(verificationType);
          handleOpenFilters(true);
        }}
        filterOrRulesForType={filterOrRulesForType}
        handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
        verificationTemplateFilterRules={selectedVerificationTemplates}
        verificationTemplates={verificationTemplatesOptionsForType}
      />
      {showFilterDropdown === verificationType && (
        <OutsideClickHandler
          onOutsideClick={() => {
            setFilterDropdown(undefined);
            handleOpenFilters(false);
            handleBulkRulesUpdate(volumeLastQueryFilterRules);
          }}
          display='contents'
          disabled={!isOutsideClickEnabled}
        >
          <StyledFilterPanel
            data-test={`verification-type-filter-panel--${verificationType}`}
            addAndRule={handleAddAndRule}
            addOrRule={handleAddOrRule}
            deleteAndRule={handleDeleteAndRule}
            filterRules={filterOrRulesForType}
            handleBulkRulesUpdate={handleBulkRulesUpdate}
            handleCancel={() => {
              setFilterDropdown(undefined);
              handleOpenFilters(false);
            }}
            handleCloseDropdown={() => setIsOutsideClickEnabled(true)}
            handleLoadMetrics={handleLoadMetrics}
            handleOpenDropdown={() => setIsOutsideClickEnabled(false)}
            lastQueryFilterRules={volumeLastQueryFilterRules}
            panelTitle={`Edit Filters - ${VERIFICATION_TYPE_NAMES_SHORT[verificationType]}`}
            updateAndRule={handleUpdateAndRule}
            verificationType={verificationType as FilteredVerificationType}
            typeDynamicOptions={dynamicFilterOptions[verificationType as FilteredVerificationType]}
          />
        </OutsideClickHandler>
      )}
    </>
  ) : (
    <StyledMetricsCard data-test={`verification-type-metrics-card--${verificationType}`}>
      <SummaryCardBody>
        <HeaderContainer>
          <StyledTitle data-test='verification-type-metrics-card--name'>
            {verificationTypeTitle}
            {/* TODO (mateo): modularize OptionsContainer to be reused by Volume (here) and in Checks */}
          </StyledTitle>
          <OptionsContainer>
            <DateGranularitySelector
              value={granularityOption}
              verificationType={verificationType}
              handleUpdateGranularity={handleUpdateGranularity}
              dateRange={[lastQueryFilters?.createdAtStart, lastQueryFilters?.createdAtEnd]}
            />
            {verificationTemplatesOptionsForType && (
              <VerificationTemplateFilter
                handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
                verificationTemplateFilterRules={selectedVerificationTemplates}
                verificationTemplates={verificationTemplatesOptionsForType}
                verificationType={verificationType}
              />
            )}
            {shouldDisplayFiltersForType && (
              <>
                <StyledButton
                  data-test={`verifications-filter-button--${verificationType}`}
                  type='button'
                  variant='basic'
                  width='auto'
                  prefixIcon={<FilterIcon />}
                  onClick={() => {
                    setFilterDropdown(verificationType);
                    handleOpenFilters(true);
                  }}
                >
                  {filterButtonCopy}
                </StyledButton>
                <DropdownMenu
                  trigger={
                    <StyledButton
                      data-test={`verifications-group-by-button--${verificationType}`}
                      id={`verification-filters-${verificationType}`}
                      type='button'
                      width='auto'
                      variant='basic'
                      prefixIcon={<LayersIcon />}
                      aria-label={groupByButtonCopy}
                    >
                      {groupByButtonCopy}
                    </StyledButton>
                  }
                  align='end'
                >
                  {groupItemsForType.map((item: DropdownItem) => (
                    <StyledDropdownMenuItem
                      id={item.id}
                      key={item.id}
                      variant='basic'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        item.onClick ? item.onClick() : undefined;
                      }}
                      selected={item.id === groupByDimensionForType}
                    >
                      {item.label}
                    </StyledDropdownMenuItem>
                  ))}
                </DropdownMenu>
              </>
            )}
          </OptionsContainer>
        </HeaderContainer>

        {showFilterDropdown === verificationType && (
          <OutsideClickHandler
            onOutsideClick={() => {
              setFilterDropdown(undefined);
              handleOpenFilters(false);
              handleBulkRulesUpdate(volumeLastQueryFilterRules);
            }}
            display='contents'
            disabled={!isOutsideClickEnabled}
          >
            <StyledFilterPanel
              addAndRule={handleAddAndRule}
              addOrRule={handleAddOrRule}
              deleteAndRule={handleDeleteAndRule}
              filterRules={filterOrRulesForType}
              handleBulkRulesUpdate={handleBulkRulesUpdate}
              handleCancel={() => {
                setFilterDropdown(undefined);
                handleOpenFilters(false);
              }}
              handleCloseDropdown={() => setIsOutsideClickEnabled(true)}
              handleLoadMetrics={handleLoadMetrics}
              handleOpenDropdown={() => setIsOutsideClickEnabled(false)}
              lastQueryFilterRules={volumeLastQueryFilterRules}
              panelTitle={`Edit Filters - ${VERIFICATION_TYPE_NAMES_SHORT[verificationType]}`}
              updateAndRule={handleUpdateAndRule}
              verificationType={verificationType as FilteredVerificationType}
              typeDynamicOptions={
                dynamicFilterOptions[verificationType as FilteredVerificationType]
              }
            />
          </OutsideClickHandler>
        )}
        <MetricsSummary data={data} />
      </SummaryCardBody>
      <VerificationMetricsChart
        barKeys={barKeys}
        filledProcessedDataForVerificationType={filledProcessedDataForVerificationType}
        groupByDimensionForType={groupByDimensionForType}
        groupItemsForType={groupItemsForType}
        groupedByInquiries={groupedByInquiries}
        groupedVerificationType={groupedVerificationType}
        lineKeys={lineKeys}
        orderedProcessedLineKeys={orderedProcessedLineKeys}
        periodicity={PERIODICTY_MAP[granularityOption]}
        processedBarKeys={processedBarKeys}
        topValues={topValues}
        topValuesVolume={topValuesVolume}
        verificationVolumeOverTime={verificationVolumeOverTime}
        chartPeriod={granularityOption}
      />
    </StyledMetricsCard>
  );
};

export default MetricsCard;
